/*
 * ------------------------------------------------------------
 * Campo de cupom de desconto do carrinho.
 *
 * Aplica com POST /cart/update.js mandando { discount: "COD1,COD2" }. Três
 * coisas não óbvias dessa rota, herdadas do tema Cadiveu:
 *
 *   1. É SUBSTITUIÇÃO, não append. Aplicar significa mandar a lista inteira já
 *      aplicada mais o novo código; remover é mandar a lista sem ele.
 *   2. Responde 200 mesmo com código inválido — o Shopify simplesmente ignora.
 *      A validação é a posteriori: inspecionar o carrinho devolvido e, se o
 *      código não pegou, reenviar a lista anterior (rollback) e avisar o
 *      cliente.
 *   3. Descobrir quais códigos estão realmente ativos exige unir três fontes do
 *      carrinho. A terceira, as alocações por item, é o que faz cupom de
 *      produto específico — que não gera desconto no nível do carrinho — ser
 *      reconhecido como válido.
 *
 * Atenção: `discount` em /cart/update.js não está na documentação oficial da
 * AJAX Cart API. Funciona na maioria das lojas, mas é comportamento não
 * contratual. Se parar de funcionar, o plano B é GET /discount/<código>, que
 * grava o cookie de desconto, seguido de um re-render das sections.
 *
 * O re-render reusa o getSectionsToRender() que <cart-drawer-items> e
 * <cart-items> já expõem, em vez de reimplementar o contrato de seletores.
 * ------------------------------------------------------------
 */

const COUPON_MESSAGES = {
  alreadyApplied: 'Cupom já aplicado',
  invalid: 'Cupom inválido ou não aplicável a este carrinho',
  applyError: 'Erro ao aplicar cupom',
  removeError: 'Erro ao remover cupom',
};

const MESSAGE_TIMEOUT = 4000;

function normalizeDiscountCodeKey(code) {
  return String(code || '').trim().toUpperCase();
}

/* União das três fontes, deduplicada pela versão maiúscula mas preservando o
   rótulo original para exibição. */
function getEffectiveDiscountCodes(cart) {
  const codes = new Map();

  const add = (code) => {
    const label = String(code || '').trim();
    if (!label) return;
    const key = normalizeDiscountCodeKey(label);
    if (!codes.has(key)) codes.set(key, label);
  };

  (cart.cart_level_discount_applications || []).forEach((application) => {
    if (application.type === 'discount_code') add(application.title);
  });

  (cart.discount_codes || []).forEach((discount) => {
    if (discount.applicable !== false) add(discount.code);
  });

  (cart.items || []).forEach((item) => {
    (item.line_level_discount_allocations || []).forEach((allocation) => {
      const application = allocation.discount_application;
      if (application && application.type === 'discount_code') add(application.title);
    });
  });

  return [...codes.values()];
}

function isCodeEffectiveOnCart(cart, code) {
  const normalized = normalizeDiscountCodeKey(code);
  return getEffectiveDiscountCodes(cart).some((applied) => normalizeDiscountCodeKey(applied) === normalized);
}

class CartDiscountCoupon extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('.cart-summary__field');
    this.input = this.querySelector('.cart-summary__input');
    this.submitButton = this.querySelector('.cart-summary__submit');
    this.messageNode = this.querySelector('[data-coupon-message]');

    if (!this.form || !this.input) return;

    this.onSubmit = this.handleSubmit.bind(this);
    this.onRemoveClick = this.handleRemoveClick.bind(this);

    this.form.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onRemoveClick);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.onSubmit);
    this.removeEventListener('click', this.onRemoveClick);
    window.clearTimeout(this.messageTimeout);
  }

  /* Os chips foram renderizados pelo Liquid no último re-render do servidor, ou
     seja, são a verdade mais recente que temos sem pagar um fetch. */
  get appliedCodes() {
    return Array.from(this.querySelectorAll('[data-coupon-remove]'))
      .map((button) => button.dataset.couponRemove)
      .filter(Boolean);
  }

  get sectionsToRender() {
    const host = document.querySelector('cart-drawer-items') || document.querySelector('cart-items');
    return host && typeof host.getSectionsToRender === 'function' ? host.getSectionsToRender() : [];
  }

  setLoading(isLoading) {
    if (!this.submitButton) return;
    this.submitButton.classList.toggle('loading', isLoading);
    this.submitButton.disabled = isLoading;
  }

  showMessage(message, isError) {
    if (!this.messageNode) return;

    this.messageNode.textContent = message;
    this.messageNode.classList.toggle('cart-coupon__message--error', Boolean(isError));
    this.messageNode.hidden = false;

    window.clearTimeout(this.messageTimeout);
    this.messageTimeout = window.setTimeout(() => {
      this.messageNode.hidden = true;
      this.messageNode.textContent = '';
    }, MESSAGE_TIMEOUT);
  }

  async submitDiscount(codes) {
    const sections = this.sectionsToRender;

    const body = JSON.stringify({
      discount: codes.join(','),
      sections: sections.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    const response = await fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
    if (!response.ok) throw new Error(`cart/update respondeu ${response.status}`);

    return response.json();
  }

  /* Mesmo laço de substituição que cart.js usa em updateQuantity. */
  renderSections(cart, sections) {
    if (!cart.sections) return;

    sections.forEach((section) => {
      const host = document.getElementById(section.id);
      if (!host) return;

      const html = cart.sections[section.section];
      if (!html) return;

      const source = new DOMParser().parseFromString(html, 'text/html').querySelector(section.selector);
      if (!source) return;

      const target = host.querySelector(section.selector) || host;
      target.innerHTML = source.innerHTML;
    });
  }

  handleSubmit(event) {
    event.preventDefault();
    if (this.isSubmitting) return;

    const code = this.input.value.trim();
    if (!code) return;

    this.apply(code);
  }

  handleRemoveClick(event) {
    const button = event.target.closest('[data-coupon-remove]');
    if (!button || this.isSubmitting) return;

    event.preventDefault();
    this.remove(button.dataset.couponRemove);
  }

  async apply(code) {
    const applied = this.appliedCodes;
    const normalized = normalizeDiscountCodeKey(code);

    if (applied.some((existing) => normalizeDiscountCodeKey(existing) === normalized)) {
      this.showMessage(COUPON_MESSAGES.alreadyApplied, true);
      return;
    }

    this.isSubmitting = true;
    this.setLoading(true);

    try {
      const sections = this.sectionsToRender;
      const cart = await this.submitDiscount([...applied, code]);

      if (!isCodeEffectiveOnCart(cart, code)) {
        /* O envio anterior pode ter derrubado os cupons que já valiam, então
           reenviamos a lista original. Depois disso o carrinho voltou ao estado
           que o DOM já mostra — não há o que re-renderizar. */
        await this.revertTo(applied);
        this.showMessage(COUPON_MESSAGES.invalid, true);
        return;
      }

      this.input.value = '';
      this.renderSections(cart, sections);
    } catch (error) {
      console.error('Erro ao aplicar cupom:', error);
      this.showMessage(COUPON_MESSAGES.applyError, true);
    } finally {
      this.isSubmitting = false;
      this.setLoading(false);
    }
  }

  async revertTo(codes) {
    try {
      await this.submitDiscount(codes);
    } catch (error) {
      console.error('Erro ao reverter cupons:', error);
    }
  }

  async remove(code) {
    const normalized = normalizeDiscountCodeKey(code);
    const remaining = this.appliedCodes.filter(
      (existing) => normalizeDiscountCodeKey(existing) !== normalized
    );

    this.isSubmitting = true;

    try {
      const sections = this.sectionsToRender;
      const cart = await this.submitDiscount(remaining);
      this.renderSections(cart, sections);
    } catch (error) {
      console.error('Erro ao remover cupom:', error);
      this.showMessage(COUPON_MESSAGES.removeError, true);
    } finally {
      this.isSubmitting = false;
    }
  }
}

customElements.define('cart-discount-coupon', CartDiscountCoupon);
